/**
 * Free-mode store: thin async wrapper over the existing localStorage
 * functions in `../projects.ts`. Promise-returning so the editor can
 * call the same API regardless of which backend is in play.
 *
 * We deliberately don't move the localStorage logic itself in here —
 * it stays in `projects.ts` because the demo-dismissed flag and the
 * last-opened pointer (also localStorage) live there too, and they're
 * not part of the project-CRUD surface.
 */

import {
  deleteProject as syncDelete,
  getProject as syncGet,
  listProjects as syncList,
  saveProject as syncSave,
  type StoredProject,
} from '../projects.js';

import type { Project, ProjectStore, ProjectSummary } from './types.js';

export const localStorageStore: ProjectStore = {
  mode: 'free',

  // eslint-disable-next-line @typescript-eslint/require-await
  async list(): Promise<ProjectSummary[]> {
    return syncList().map(toSummary);
  },

  // eslint-disable-next-line @typescript-eslint/require-await
  async get(id: string): Promise<Project | null> {
    const row = syncGet(id);
    return row ? toProject(row) : null;
  },

  // eslint-disable-next-line @typescript-eslint/require-await
  async save(project: Project): Promise<Project> {
    syncSave(project);
    // syncSave mutates updatedAt internally; re-fetch to pick that up.
    const refreshed = syncGet(project.id);
    return refreshed ? toProject(refreshed) : project;
  },

  // eslint-disable-next-line @typescript-eslint/require-await
  async delete(id: string): Promise<void> {
    syncDelete(id);
  },
};

function toProject(p: StoredProject): Project {
  // Free-mode projects don't carry server-side fields; the type system
  // already permits the extras to be absent.
  return p;
}

function toSummary(p: StoredProject): ProjectSummary {
  const summary: ProjectSummary = {
    id: p.id,
    name: p.name,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  };
  if (p.deployTarget) summary.deployTargetKind = p.deployTarget.kind;
  return summary;
}
